import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Layout,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import PageLayout from "../../components/common/Layouts/PageLayout";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  resetCreateStatus,
  resetUpdateStatus,
  resetDeleteStatus,
} from "../../redux/categorySlice";

const { Content } = Layout;

export default function CategoriesPage() {
  const dispatch = useDispatch();
  const [modal, contextHolder] = Modal.useModal();
  const [form] = Form.useForm();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const categories = useSelector((state) => state.category.categories);
  const fetchStatus = useSelector((state) => state.category.fetchStatus);
  const createStatus = useSelector((state) => state.category.createStatus);
  const updateStatus = useSelector((state) => state.category.updateStatus);
  const deleteStatus = useSelector((state) => state.category.deleteStatus);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Re-fetch after mutations succeed
  useEffect(() => {
    if (createStatus === "succeeded") {
      message.success("Category created successfully");
      dispatch(resetCreateStatus());
      dispatch(fetchCategories());
      setModalOpen(false);
      form.resetFields();
    }
  }, [createStatus, dispatch, form]);

  useEffect(() => {
    if (updateStatus === "succeeded") {
      message.success("Category updated successfully");
      dispatch(resetUpdateStatus());
      dispatch(fetchCategories());
      setModalOpen(false);
      form.resetFields();
    }
  }, [updateStatus, dispatch, form]);

  useEffect(() => {
    if (deleteStatus === "succeeded") {
      message.success("Category deleted successfully");
      dispatch(resetDeleteStatus());
      dispatch(fetchCategories());
    }
  }, [deleteStatus, dispatch]);

  const openAddModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingCategory(record);
    form.setFieldsValue({ name: record.name, slug: record.slug });
    setModalOpen(true);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingCategory) {
        dispatch(updateCategory(editingCategory.id, values));
      } else {
        dispatch(createCategory(values));
      }
    });
  };

  const handleDelete = (record) => {
    modal.confirm({
      title: "Delete this category?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          Category <strong>{record.name}</strong> will be permanently deleted.
          Events in this category will lose their category assignment.
        </>
      ),
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        dispatch(deleteCategory(record.id));
      },
    });
  };

  const isSaving = createStatus === "loading" || updateStatus === "loading";

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#000738" }}>{text}</span>
      ),
    },
    {
      title: "URL Slug",
      dataIndex: "slug",
      key: "slug",
      render: (text) => (
        <code style={{ color: "#555", fontSize: 13 }}>{text || "—"}</code>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: () => <Tag color="green">Active</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record)}
            loading={deleteStatus === "loading"}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  };

  return (
    <PageLayout title="Event Categories">
      {contextHolder}

      <Content className="contentWrap">
        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <h2 style={{ margin: 0, color: "#000738", fontSize: 20, fontWeight: 600 }}>
              Event Categories
            </h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddModal}
              style={{ backgroundColor: "#61A0FF", borderColor: "#61A0FF" }}
            >
              Add Category
            </Button>
          </div>

          <div style={cardStyle}>
            <Table
              columns={columns}
              dataSource={categories.map((c) => ({ ...c, key: c.id }))}
              loading={fetchStatus === "loading"}
              pagination={{
                pageSize: 15,
                showSizeChanger: false,
                showTotal: (total) => `${total} categories`,
              }}
              bordered={false}
            />
          </div>
        </div>
      </Content>

      <Modal
        title={editingCategory ? "Edit Category" : "Add Category"}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        okText={editingCategory ? "Update" : "Create"}
        confirmLoading={isSaving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Please enter a category name" }]}
          >
            <Input placeholder="e.g. Music, Technology, Food & Drinks" size="large" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="URL Slug"
            rules={[
              { required: true, message: "Please enter a URL slug" },
              {
                pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                message: "Use lowercase letters, numbers, and hyphens only",
              },
            ]}
            extra="Used in category page URLs, e.g. /events/category/music"
          >
            <Input placeholder="e.g. music, food-and-drinks" size="large" />
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
